import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, Download, Sigma, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { NodeSelectorModal } from "@/components/NodeSelectorModal";
import { PmlResumenModal } from "@/components/PmlResumenModal";
import {
  downloadPmlPreciosCsv,
  getPmlCatalogos,
  getPmlPrecios,
  type Mercado,
  type NodoTipo,
  type PmlPreciosFilters,
} from "@/lib/api";
import { formatDate } from "@/lib/utils";

type SelectedNode = { tipo: NodoTipo; clave: string };

const PAGE_SIZE = 100;
const STORAGE_KEY_NODES = "portal-edocta:pml-selected-nodes";

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function isoLocal(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function currentMonthRange(): { desde: string; hasta: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { desde: isoLocal(first), hasta: isoLocal(last) };
}

function loadStoredNodes(): SelectedNode[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_NODES);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (n) =>
        n &&
        typeof n.clave === "string" &&
        (n.tipo === "PML" || n.tipo === "PND"),
    );
  } catch {
    return [];
  }
}

const PRECIO_FMT = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function fmt(value: string | null): string {
  if (value == null) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return PRECIO_FMT.format(n);
}

const COLUMNS: { key: string; label: string; align?: "right" }[] = [
  { key: "tipo", label: "Tipo" },
  { key: "clave", label: "Nodo / Zona" },
  { key: "sistema", label: "Sistema" },
  { key: "fecha", label: "Fecha" },
  { key: "hora", label: "Hora", align: "right" },
  { key: "precio", label: "Precio", align: "right" },
  { key: "ene", label: "Energía", align: "right" },
  { key: "per", label: "Pérdidas", align: "right" },
  { key: "cng", label: "Congestión", align: "right" },
];

export function PmlPndPage() {
  const defaultRange = useMemo(currentMonthRange, []);
  const [mercado, setMercado] = useState<Mercado>("MDA");
  const [desde, setDesde] = useState<string>(defaultRange.desde);
  const [hasta, setHasta] = useState<string>(defaultRange.hasta);
  const [selected, setSelected] = useState<SelectedNode[]>(loadStoredNodes);
  const [modalOpen, setModalOpen] = useState(false);
  const [resumenOpen, setResumenOpen] = useState(false);
  const [sort, setSort] = useState<string>("fecha");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);
  const [downloading, setDownloading] = useState(false);

  // Persistir la selección de nodos en localStorage por máquina.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_NODES, JSON.stringify(selected));
    } catch {
      // localStorage puede estar lleno o deshabilitado — silenciar.
    }
  }, [selected]);

  const catalogos = useQuery({
    queryKey: ["pml-catalogos", mercado],
    queryFn: () => getPmlCatalogos(mercado),
  });

  const filters: PmlPreciosFilters = useMemo(
    () => ({
      mercado,
      nodos: selected,
      desde: desde || undefined,
      hasta: hasta || undefined,
      sort,
      order,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [mercado, selected, desde, hasta, sort, order, page],
  );

  const precios = useQuery({
    queryKey: ["pml-precios", filters],
    queryFn: () => getPmlPrecios(filters),
    enabled: selected.length > 0,
  });

  const totalPages = precios.data ? Math.ceil(precios.data.total / PAGE_SIZE) : 0;

  const handleSort = (col: string) => {
    if (sort === col) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setSort(col);
      setOrder("asc");
    }
    setPage(0);
  };

  const removeNode = (n: SelectedNode) => {
    setSelected(selected.filter((s) => !(s.tipo === n.tipo && s.clave === n.clave)));
    setPage(0);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Field label="Mercado">
            <div className="inline-flex border rounded overflow-hidden w-full">
              {(["MDA", "MTR"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setMercado(m);
                    setPage(0);
                  }}
                  className={`flex-1 px-3 py-1.5 text-sm ${
                    mercado === m
                      ? "bg-blue-600 text-white"
                      : "bg-white hover:bg-slate-50"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Desde">
            <input
              type="date"
              className="border rounded px-2 py-1.5 w-full"
              value={desde}
              min={catalogos.data?.fecha_min ?? undefined}
              max={catalogos.data?.fecha_max ?? undefined}
              onChange={(e) => {
                setDesde(e.target.value);
                setPage(0);
              }}
            />
          </Field>
          <Field label="Hasta">
            <input
              type="date"
              className="border rounded px-2 py-1.5 w-full"
              value={hasta}
              min={catalogos.data?.fecha_min ?? undefined}
              max={catalogos.data?.fecha_max ?? undefined}
              onChange={(e) => {
                setHasta(e.target.value);
                setPage(0);
              }}
            />
          </Field>
          <div className="md:col-span-2 flex items-end gap-2">
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50 flex-1"
            >
              Seleccionar nodos…
            </button>
            <button
              type="button"
              disabled={selected.length === 0}
              onClick={() => setResumenOpen(true)}
              className="inline-flex items-center gap-1 border rounded px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Promedio anual, mensual y horario de los nodos seleccionados"
            >
              <Sigma className="w-4 h-4" />
              Resumen
            </button>
            <button
              type="button"
              disabled={selected.length === 0 || downloading}
              onClick={async () => {
                setDownloading(true);
                try {
                  await downloadPmlPreciosCsv({
                    mercado,
                    nodos: selected,
                    desde: desde || undefined,
                    hasta: hasta || undefined,
                    sort,
                    order,
                  });
                } catch (err) {
                  alert(`Error al descargar: ${(err as Error).message}`);
                } finally {
                  setDownloading(false);
                }
              }}
              className="inline-flex items-center gap-1 border rounded px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Descargar resultado completo en CSV"
            >
              <Download className="w-4 h-4" />
              {downloading ? "Descargando…" : "CSV"}
            </button>
            <button
              type="button"
              onClick={() => {
                const r = currentMonthRange();
                setDesde(r.desde);
                setHasta(r.hasta);
                setSelected([]);
                setPage(0);
              }}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
              title="Restablece fechas al mes actual y borra nodos seleccionados"
            >
              Limpiar
            </button>
          </div>
        </div>

        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-2 border-t">
            {selected.map((n) => (
              <span
                key={`${n.tipo}:${n.clave}`}
                className={`inline-flex items-center gap-1 text-xs rounded px-2 py-0.5 ${
                  n.tipo === "PML"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-amber-100 text-amber-700"
                }`}
              >
                <span className="font-medium">{n.tipo}</span>
                <span className="font-mono">{n.clave}</span>
                <button
                  type="button"
                  onClick={() => removeNode(n)}
                  className="hover:text-slate-900"
                  aria-label="Quitar"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-left">
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  className={`px-3 py-2 font-medium select-none cursor-pointer hover:bg-slate-200 ${
                    c.align === "right" ? "text-right" : ""
                  }`}
                  onClick={() => handleSort(c.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    <SortIcon active={sort === c.key} order={order} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {selected.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-8 text-center text-slate-500">
                  Selecciona al menos un nodo para consultar.
                </td>
              </tr>
            )}
            {selected.length > 0 && precios.isLoading && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-8 text-center text-slate-500">
                  Cargando…
                </td>
              </tr>
            )}
            {precios.isError && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-8 text-center text-red-600">
                  Error: {(precios.error as Error).message}
                </td>
              </tr>
            )}
            {precios.data?.items.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-3 py-8 text-center text-slate-500">
                  Sin resultados para los filtros aplicados.
                </td>
              </tr>
            )}
            {precios.data?.items.map((row, i) => (
              <tr key={i} className="border-t hover:bg-slate-50">
                <Td>
                  <span
                    className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                      row.tipo === "PML"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {row.tipo}
                  </span>
                </Td>
                <Td className="font-mono text-xs">{row.clave}</Td>
                <Td>{row.sistema}</Td>
                <Td>{formatDate(row.fecha)}</Td>
                <Td className="text-right tabular-nums">{row.hora}</Td>
                <Td className="text-right tabular-nums font-medium">{fmt(row.precio)}</Td>
                <Td className="text-right tabular-nums text-slate-600">{fmt(row.ene)}</Td>
                <Td className="text-right tabular-nums text-slate-600">{fmt(row.per)}</Td>
                <Td className="text-right tabular-nums text-slate-600">{fmt(row.cng)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {precios.data && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-slate-500">
            {precios.data.total.toLocaleString("es-MX")} resultados · página {page + 1} de {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="border rounded px-3 py-1 disabled:opacity-50"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Anterior
            </button>
            <button
              type="button"
              className="border rounded px-3 py-1 disabled:opacity-50"
              onClick={() => setPage((p) => p + 1)}
              disabled={page + 1 >= totalPages}
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}

      <NodeSelectorModal
        open={modalOpen}
        initialSelection={selected}
        onClose={() => setModalOpen(false)}
        onApply={(sel) => {
          setSelected(sel);
          setPage(0);
        }}
      />

      <PmlResumenModal
        open={resumenOpen}
        onClose={() => setResumenOpen(false)}
        mercado={mercado}
        nodos={selected}
        desde={desde || undefined}
        hasta={hasta || undefined}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-1.5 ${className}`}>{children}</td>;
}

function SortIcon({ active, order }: { active: boolean; order: "asc" | "desc" }) {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
  return order === "asc" ? (
    <ArrowUp className="w-3 h-3" />
  ) : (
    <ArrowDown className="w-3 h-3" />
  );
}
